import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Header from '@/components/Header';
import NoGroupHome from '@/components/home/NoGroupHome';

import SkillRatingPrompt from '@/components/SkillRatingPrompt';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Users, Trophy, CalendarDays, Medal } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useIsMobile, MOBILE_BREAKPOINT } from '@/hooks/use-mobile';
import FriendsSheet from '@/components/FriendsSheet';
import { GroupSelectionDialog } from '@/components/polls/GroupSelectionDialog';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSkillRatingCheck } from '@/hooks/useSkillRatingCheck';
import { useScheduledMatches } from '@/hooks/useScheduledMatches';
import { useUserActivePolls } from '@/hooks/useUserActivePolls';
import { motion } from 'framer-motion';

import { TournamentGroupSelectionDialog } from '@/components/tournament/TournamentGroupSelectionDialog';
import { NotificationPermissionPrompt } from '@/components/NotificationPermissionPrompt';
import { PWAInstallBanner } from '@/components/PWAInstallBanner';

// Import components
import { QuickActionsBar } from '@/components/home/QuickActionsBar';
import { ActivePollsWidget } from '@/components/home/ActivePollsWidget';
import { UpcomingMatchesWidget } from '@/components/home/UpcomingMatchesWidget';
import { OpenMatchesWidget } from '@/components/home/OpenMatchesWidget';
import { SuggestedPairingsWidget } from '@/components/home/SuggestedPairingsWidget';
import { ChallengeMatchesWidget } from '@/components/home/ChallengeMatchesWidget';

import { DiscoverGroupsWidget } from '@/components/home/DiscoverGroupsWidget';
import { DiscoverPlayersWidget } from '@/components/home/DiscoverPlayersWidget';
import { DiscoverMatchesWidget } from '@/components/home/DiscoverMatchesWidget';
import { DiscoverTournamentsWidget } from '@/components/home/DiscoverTournamentsWidget';
import { useDiscoveryEngine } from '@/hooks/useDiscoveryEngine';
import { HeroActions } from '@/components/home/HeroActions';
import MatchLimitBanner from '@/components/premium/MatchLimitBanner';
import { UpcomingTournamentsWidget } from '@/components/home/UpcomingTournamentsWidget';
import { useUpcomingTournaments } from '@/hooks/useUpcomingTournaments';
import { WelcomeBanner } from '@/components/home/WelcomeBanner';
import { RecentFeedWidget } from '@/components/home/RecentFeedWidget';
import { FirstRunChecklist } from '@/components/home/FirstRunChecklist';
import { DashboardTour } from '@/components/home/DashboardTour';
import { ProfileCompletionBanner } from '@/components/home/ProfileCompletionBanner';
import { DesktopUtilityRail } from '@/components/home/DesktopUtilityRail';
import { useUserGroupsOptimized } from '@/hooks/useUserGroupsOptimized';
import { usePlayerStats } from '@/hooks/usePlayerStats';
import { markFirstSessionSeen } from '@/utils/firstRun';
import { getMissingProfileFields } from '@/utils/profileCompleteness';

const Index = () => {
  const { profile, user, ready } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isFriendsSheetOpen, setIsFriendsSheetOpen] = useState(false);
  const [isGroupSelectionOpen, setIsGroupSelectionOpen] = useState(false);
  const [isTournamentDialogOpen, setIsTournamentDialogOpen] = useState(false);
  const [dashboardTourCompletedAt, setDashboardTourCompletedAt] = useState<string | null>(null);
  const { showSkillRatingPrompt, dismissPrompt } = useSkillRatingCheck();
  const [homeTab, setHomeTab] = useState<string>(() => {
    try { return localStorage.getItem('luvall-home-tab') || 'play'; } catch { return 'play'; }
  });
  const changeHomeTab = (v: string) => {
    setHomeTab(v);
    try { localStorage.setItem('luvall-home-tab', v); } catch { /* ignore */ }
  };

  // Get scheduled matches
  const { scheduledMatches, loading: scheduledLoading } = useScheduledMatches();

  // Get active polls
  const { polls: activePolls, loading: pollsLoading, respondToPoll, withdrawResponse } = useUserActivePolls();

  // Get upcoming tournaments
  const { data: upcomingTournaments, isLoading: tournamentsLoading } = useUpcomingTournaments();

  // Discovery engine
  const { data: discoveryData } = useDiscoveryEngine();


  // Query to check for matches that need validation by the current user
  const { data: pendingValidationMatches } = useQuery({
    queryKey: ['pending-validation-matches', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data: participatingMatches, error: participatingError } = await supabase
        .from('match_players')
        .select('match_id')
        .eq('user_id', user.id);
      
      if (participatingError) throw participatingError;
      
      const matchIds = participatingMatches.map(item => item.match_id).filter(Boolean);
      if (matchIds.length === 0) return [];

      const { data: matches, error: matchesError } = await supabase
        .from('matches')
        .select('*')
        .eq('validation_status', 'pending')
        .in('id', matchIds);
      
      if (matchesError) throw matchesError;

      const validatableMatches = matches.filter(match => match.created_by !== user.id);
      return validatableMatches;
    },
    enabled: !!user
  });

  // Mobile: signed-out visitors see the public home (marketing) page instead of
  // being pushed straight into onboarding/login.
  useEffect(() => {
    if (!user && isMobile && ready) {
      // Delay redirect to ensure viewport detection is stable
      const timeoutId = setTimeout(() => {
        if (window.innerWidth < MOBILE_BREAKPOINT) {
          navigate('/', { replace: true });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [user, isMobile, ready, navigate]);

  // First-run setup signals (lightweight, cached queries)
  const { groups: userGroups, isLoading: groupsLoading } = useUserGroupsOptimized(user?.id);
  const { stats: playerStats, loading: statsLoading } = usePlayerStats(user?.id);

  const needsSkillRating =
    !!profile &&
    (profile.skill_assessment_required === true || profile.skill_rating_defined !== true);

  const missingProfileFields = useMemo(() => {
    if (!profile) return [];
    return getMissingProfileFields(profile);
  }, [profile]);

  useEffect(() => {
    setDashboardTourCompletedAt(profile?.dashboard_tour_completed_at ?? null);
  }, [profile?.dashboard_tour_completed_at]);


  // Mark the first session as seen once the home screen has rendered, so
  // deferred prompts (skill assessment) can appear from the next visit on.
  useEffect(() => {
    if (user && ready) markFirstSessionSeen();
  }, [user, ready]);


  // Stabilize callback functions with useCallback
  const handleCreatePollClick = useCallback(() => {
    if (!user) {
      navigate('/auth');
    } else {
      setIsGroupSelectionOpen(true);
    }
  }, [user, navigate]);

  const handleFindPlayersClick = useCallback(() => {
    setIsFriendsSheetOpen(true);
  }, []);

  const handleCreateTournamentClick = useCallback(() => {
    if (!user) {
      navigate('/auth');
    } else {
      setIsTournamentDialogOpen(true);
    }
  }, [user, navigate]);



  const handleSkillPromptTrigger = () => {
    // This will be handled by the existing SkillRatingPrompt component
    // We just need to make sure it shows when needed
  };

  // Show loading state if auth is not ready
  if (!ready) {
    return (
      <div className="pb-safe-nav w-full">
        <Header title="LuvALL" showNotifications={false} />
          <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="sr-only">{t('home.loading')}</p>
        </div>
      </div>
    );
  }

  // If user is not logged in, show marketing layout (desktop only, mobile redirects above)
  if (!user) {
    return (
      <div className="pb-safe-nav w-full">
        <Header title="LuvALL" showNotifications={false} />
        
        {/* Hero Section */}
        <div className="px-4 py-8 mb-8">
          <div className="relative rounded-2xl overflow-hidden shadow-xl" style={{
            backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.2)), url('/lovable-uploads/b2e925bd-c913-40f6-9817-619d4e5dae63.png')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}>
            <div className="p-6 sm:p-8 md:p-12 text-center bg-gradient-to-b from-transparent to-black/40">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-3xl mx-auto py-8 sm:py-12 text-white"
              >
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="text-4xl sm:text-5xl font-bold mb-4"
                >
                  {t('home.welcome')} <span className="text-padel-green-light">LuvALL</span>
                </motion.h1>
                
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="text-white/95 mb-8 sm:mb-10 max-w-2xl mx-auto text-lg sm:text-xl leading-relaxed"
                >
                  {t('home.tagline')}
                </motion.p>
                
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.6 }}
                >
                  <MatchLimitBanner />
                  <HeroActions user={user} />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
        
        {/* Features Section */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="px-4 pt-4 pb-12"
        >
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="text-3xl font-bold text-center mb-12 text-foreground"
          >
            {t('home.keyFeatures')}
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-card p-6 rounded-xl shadow-sm border border-border flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-padel-green-light/20 rounded-full flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-padel-green-dark" />
              </div>
              <h3 className="text-lg font-medium mb-2 text-foreground">{t('home.communityGroups')}</h3>
              <p className="text-muted-foreground text-sm mb-5">
                {t('home.communityGroupsDesc')}
              </p>
              <Button variant="ghost" className="text-primary hover:text-primary/90 mt-auto" onClick={() => navigate('/auth')}>
                {t('home.getStarted')}
              </Button>
            </div>
            
            <div className="bg-card p-6 rounded-xl shadow-sm border border-border flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-4">
                <Trophy className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2 text-foreground">{t('home.matchPlay')}</h3>
              <p className="text-muted-foreground text-sm mb-5">
                {t('home.matchPlayDesc')}
              </p>
              <Button variant="ghost" className="text-primary hover:text-primary/90 mt-auto" onClick={() => navigate('/auth')}>
                {t('home.signUp')}
              </Button>
            </div>
            
            <div className="bg-card p-6 rounded-xl shadow-sm border border-border flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2 text-foreground">{t('home.connectPlayers')}</h3>
              <p className="text-muted-foreground text-sm mb-5">
                {t('home.connectPlayersDesc')}
              </p>
              <Button variant="ghost" className="text-primary hover:text-primary/90 mt-auto" onClick={() => navigate('/auth')}>
                {t('home.joinNow')}
              </Button>
            </div>
          </div>
        </motion.div>
        
      </div>
    );
  }

  // Logged-in user dashboard layout
  return (
    <div className="pb-safe-nav w-full">
      <Header title="LuvALL" showNotifications={true} />
      
      {/* Skill Rating Prompt */}
      {showSkillRatingPrompt && <SkillRatingPrompt onDismiss={dismissPrompt} />}
      
      {/* Notification Permission Prompt */}
      <NotificationPermissionPrompt />
      
      {/* Dynamic Welcome Banner with Carousel Tasks */}
      {profile && (
        <div data-tour="welcome-banner">
          <WelcomeBanner
            profile={profile}
            scheduledMatches={scheduledMatches}
            activePolls={activePolls}
            upcomingTournaments={[
              ...(upcomingTournaments?.participating || []).map(t => ({ ...t, isParticipating: true })),
              ...(upcomingTournaments?.available || []).map(t => ({ ...t, isParticipating: false })),
            ]}
            pendingValidations={pendingValidationMatches?.length || 0}
            showSkillPrompt={showSkillRatingPrompt}
            onSkillPrompt={handleSkillPromptTrigger}
            loading={scheduledLoading || pollsLoading || tournamentsLoading}
          />

        </div>
      )}

      {/* Quick Actions Bar */}
      <div data-tour="quick-actions">
        <QuickActionsBar 
          onCreatePoll={handleCreatePollClick}
          onFindPlayers={handleFindPlayersClick}
          onCreateTournament={handleCreateTournamentClick}
        />
      </div>

      {/* First-run setup guidance for new users (only once signals are settled) */}
      {profile && !groupsLoading && !statsLoading && (
        <div data-tour="first-run-checklist">
          <FirstRunChecklist
            profileComplete={missingProfileFields.length === 0 && !needsSkillRating}
            hasGroup={(userGroups?.length || 0) > 0}
            hasPlayed={(playerStats?.totalMatches || 0) > 0}
          />
        </div>
      )}

      {/* Non-blocking nudge for deferrable profile fields */}
      {profile && !showSkillRatingPrompt && (
        <ProfileCompletionBanner missingFields={missingProfileFields} />
      )}

      {/* First-time spotlight walkthrough */}
      <DashboardTour
        enabled={!!profile && !showSkillRatingPrompt}
        userId={user?.id}
        completedAt={dashboardTourCompletedAt}
        onCompleted={setDashboardTourCompletedAt}
      />

      {/* Intent-based sections: Play / Compete / Community */}
      <div className="px-4 xl:grid xl:grid-cols-[minmax(0,1.85fr)_minmax(280px,1fr)] xl:items-start xl:gap-6">
        <Tabs value={homeTab} onValueChange={changeHomeTab} className="min-w-0 w-full">
          <TabsList className="grid w-full grid-cols-3 h-12 mb-4">
            <TabsTrigger value="play" className="min-h-[44px] gap-1.5">
              <CalendarDays className="h-4 w-4" /> Play
              {activePolls.length > 0 && <Badge className="h-5 min-w-5 px-1 text-xs">{activePolls.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="compete" className="min-h-[44px] gap-1.5" data-tour="challenge-matches">
              <Trophy className="h-4 w-4" /> Compete
            </TabsTrigger>
            <TabsTrigger value="community" className="min-h-[44px] gap-1.5">
              <Users className="h-4 w-4" /> Community
            </TabsTrigger>
          </TabsList>

          <TabsContent value="play" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-0">
            {!groupsLoading && (userGroups?.length || 0) === 0 && <NoGroupHome />}
            <OpenMatchesWidget userLocation={profile?.location} />
            <div data-tour="active-polls">
              <ActivePollsWidget
                polls={activePolls}
                loading={pollsLoading}
                onRespond={respondToPoll}
                onWithdraw={withdrawResponse}
                currentUserId={user?.id}
              />
            </div>
            <div data-tour="upcoming-matches">
              <UpcomingMatchesWidget matches={scheduledMatches} loading={scheduledLoading} />
            </div>
            <SuggestedPairingsWidget />
          </TabsContent>

          <TabsContent value="compete" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-0">
            <div>
              <ChallengeMatchesWidget />
            </div>
            {!tournamentsLoading &&
             (upcomingTournaments?.participating.length > 0 ||
              upcomingTournaments?.available.length > 0) && (
              <UpcomingTournamentsWidget tournaments={upcomingTournaments} loading={false} />
            )}
            <Button variant="outline" className="min-h-[44px] justify-start gap-2" onClick={() => navigate('/leagues')}>
              <Medal className="h-4 w-4" /> My leagues
            </Button>
          </TabsContent>

          <TabsContent value="community" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-0">
            {discoveryData?.groups && <DiscoverGroupsWidget groups={discoveryData.groups} />}
            {discoveryData?.players && <DiscoverPlayersWidget players={discoveryData.players} />}
            {discoveryData?.openMatches && <DiscoverMatchesWidget matches={discoveryData.openMatches} />}
            {discoveryData?.tournaments && <DiscoverTournamentsWidget tournaments={discoveryData.tournaments} />}
            <RecentFeedWidget />
          </TabsContent>
        </Tabs>
        <DesktopUtilityRail polls={activePolls} groups={userGroups || []} onRespond={respondToPoll} />
      </div>
      
      {/* Dialogs */}
      <FriendsSheet isOpen={isFriendsSheetOpen} onOpenChange={setIsFriendsSheetOpen} />
      <GroupSelectionDialog 
        open={isGroupSelectionOpen} 
        onOpenChange={setIsGroupSelectionOpen}
      />
      <TournamentGroupSelectionDialog 
        open={isTournamentDialogOpen} 
        onOpenChange={setIsTournamentDialogOpen}
      />
      
      <PWAInstallBanner />
    </div>
  );
};

export default Index;
